import { useCallback } from 'react';
import { API_URL } from '../../../utils/api';

/**
 * Hook för hantering av save/send-funktioner för estimat
 * Separerar komplex affärslogik från UI-komponenten
 */
/**
 * ⚠️ variantKey MÅSTE följa med i varje sparning.
 *
 * Sparningen går mot leadet (PUT /api/estimat/lead/:leadId), inte mot ett
 * specifikt estimat-id. Utan variantKey väljer backend standardspåret — och
 * redigerar man då ett duplicerat estimat hamnar ändringarna på originalet.
 */
const useEstimatSaveActions = ({
  variantKey = null,
  // Data
  leadId,
  currentEstimatId,
  currentVersion,
  currentStatus,
  currentProjectId,
  formData,
  dynamicNotes,
  roomMeasurements,
  originalEstimatKategorier,
  hasUnsavedChanges,
  hasUnsavedNotes,
  // Setters
  setCurrentEstimatId,
  setCurrentVersion,
  setCurrentStatus,
  setCurrentSentAt,
  setCurrentProjectId,
  setOriginalEstimatKategorier,
  setHasUnsavedChanges,
  setHasUnsavedNotes,
  setSaving,
  // Helpers
  saveDynamicNotes,
  saveIntervjuFragor,
  isActuallySent,
  formatBeställare2ForAPI,
  invalidateCache,
  navigate,
  // Modals
  setShowSuccessModal,
  setModalIsUpdate,
}) => {
  
  /**
   * Spara estimat (manuellt eller via Ctrl+S)
   */
  const handleSaveEstimat = useCallback(async () => {
    if (!hasUnsavedChanges && !hasUnsavedNotes) {
      return;
    }

    try {
      setSaving(true);
      
      // Spara anteckningar om det finns osparade anteckningar
      if (hasUnsavedNotes) {
        if (currentStatus === 'draft') {
          // För draft: anteckningar sparas med resten av estimatet
        } else {
          // För skickade estimat: spara anteckningar separat
          await saveDynamicNotes();
          await saveIntervjuFragor();
          setHasUnsavedNotes(false);
        }
      }
      
      /**
       * För utkast sparas anteckningarna tillsammans med estimatet längre ner
       * (intervjuFrågor: { ...formData.intervjuFrågor, dynamicNotes }). Returnerade
       * vi här när bara anteckningar ändrats nåddes den koden aldrig — knappen var
       * klickbar, ingenting sparades, och allt försvann när användaren lämnade sidan.
       */
      const behoverSparaEstimat = hasUnsavedChanges || (hasUnsavedNotes && currentStatus === 'draft');
      if (!behoverSparaEstimat) {
        setSaving(false);
        return;
      }
      
      const token = localStorage.getItem('token');
      
      // Om estimatet redan skickats tidigare, använd updateEstimat för att automatiskt öka versionen
      if (isActuallySent() && currentEstimatId) {
        // Öka versionen BARA om estimat-poster (estimatKategorier) har ändrats
        // Ignorera sortOrder-fältet — ordningsändringar ska INTE trigga ny version
        const stripSortOrder = (obj) => JSON.stringify(obj, (key, val) => key === 'sortOrder' ? undefined : val);
        const estimatDataHasChanged = stripSortOrder(originalEstimatKategorier) !== stripSortOrder(formData.estimat);
        const shouldIncrementVersion = estimatDataHasChanged;
        
        const response = await fetch(`${API_URL}/api/estimat/${currentEstimatId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            beställare2: formatBeställare2ForAPI(formData.beställare2),
            projectInfo: formData.projectInfo,
            intervjuFrågor: currentStatus === 'draft' ? { ...formData.intervjuFrågor, dynamicNotes } : undefined,
            roomMeasurements: currentStatus === 'draft' ? roomMeasurements : undefined,
            estimatKategorier: formData.estimat,
            projectId: currentProjectId,
            incrementVersion: shouldIncrementVersion
          }),
        });
        
        if (response.ok) {
          const updatedEstimat = await response.json();
          const newVersion = Number(updatedEstimat.version || currentVersion);
          const newStatus = updatedEstimat.status || currentStatus;
          const newEstimatId = updatedEstimat._id || currentEstimatId;
          setCurrentVersion(newVersion);
          setCurrentStatus(newStatus);
          setCurrentEstimatId(newEstimatId);
          setHasUnsavedChanges(false);
          setHasUnsavedNotes(false);
          setOriginalEstimatKategorier(JSON.parse(JSON.stringify(formData.estimat)));
        } else {
          throw new Error('Fel vid sparande av estimat');
        }
      } else {
        // Vanlig sparning för estimat som inte skickats än
        const response = await fetch(`${API_URL}/api/estimat/lead/${leadId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            variantKey, // vilket estimatspår som redigeras
            beställare2: formatBeställare2ForAPI(formData.beställare2),
            projectInfo: formData.projectInfo,
            intervjuFrågor: currentStatus === 'draft' ? { ...formData.intervjuFrågor, dynamicNotes } : undefined,
            roomMeasurements: currentStatus === 'draft' ? roomMeasurements : undefined,
            estimatKategorier: formData.estimat
          }),
        });
        
        if (response.ok) {
          const savedEstimat = await response.json();
          // Sätt currentEstimatId så att auto-save (on-blur) fungerar efter första sparningen
          if (savedEstimat._id) {
            setCurrentEstimatId(savedEstimat._id);
          }
          if (savedEstimat.version) {
            setCurrentVersion(Number(savedEstimat.version));
          }
          if (savedEstimat.status) {
            setCurrentStatus(savedEstimat.status);
          }
          setHasUnsavedChanges(false);
          setHasUnsavedNotes(false);
          setOriginalEstimatKategorier(JSON.parse(JSON.stringify(formData.estimat)));
        } else {
          throw new Error('Fel vid sparande av estimat');
        }
      }
    } catch (error) {
      console.error('Fel vid sparande av estimat:', error);
      alert('Fel vid sparande av estimat. Försök igen.');
    } finally {
      setSaving(false);
    }
  }, [
    // variantKey MÅSTE vara med: se filens rubrik. Frys den i callbacken och en
    // sparning efter ett spårbyte hamnar på fel estimatspår.
    variantKey,
    hasUnsavedChanges,
    hasUnsavedNotes,
    setSaving,
    currentStatus,
    saveDynamicNotes,
    saveIntervjuFragor,
    setHasUnsavedNotes,
    isActuallySent,
    currentEstimatId,
    originalEstimatKategorier,
    formData,
    formatBeställare2ForAPI,
    dynamicNotes,
    roomMeasurements,
    currentProjectId,
    currentVersion,
    setCurrentVersion,
    setCurrentStatus,
    setCurrentEstimatId,
    setHasUnsavedChanges,
    setOriginalEstimatKategorier,
    leadId
  ]);

  /**
   * Skicka estimat till kund
   * @param {string} versionComment - Valfri kommentar som beskriver ändringar (för uppdateringar)
   * @param {string} customerIntroMessage - Valfritt intro-meddelande som visas högst upp i estimatet
   */
  // extraEstimat = andra parallella estimat som ska skickas i samma vända
  // ({ _id, variantName }). De skickas FÖRE huvudestimatet med notiser
  // avstängda, så kunden får ett enda mail och en enda notis.
  const handleSendEstimat = useCallback(async (versionComment = '', customerIntroMessage = '', extraEstimat = []) => {
    // Hur långt flödet hann. Läses i catch-blocket.
    //
    // Sparningen och utskicket är separata anrop, så ett avbrott mitt i lämnar
    // ett halvfärdigt läge. Utan de här flaggorna fick BK ett enda "Fel vid
    // skickande, försök igen" och kunde inte veta om ändringarna låg kvar eller
    // om kunden redan fått mail — vilket är skillnaden mellan att försöka igen
    // och att råka skicka dubbelt.
    let hannSpara = false;
    let antalOvrigaSkickade = 0;
    let huvudestimatSkickat = false;

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const wasAlreadySent = currentVersion > 1.0;
      let actualEstimatId = currentEstimatId;
      
      // ALLTID spara innan sändning
      if (!isActuallySent()) {
        const response = await fetch(`${API_URL}/api/estimat/lead/${leadId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            beställare2: formatBeställare2ForAPI(formData.beställare2),
            projectInfo: formData.projectInfo,
            intervjuFrågor: currentStatus === 'draft' ? { ...formData.intervjuFrågor, dynamicNotes } : undefined,
            roomMeasurements: currentStatus === 'draft' ? roomMeasurements : undefined,
            estimatKategorier: formData.estimat,
            variantKey, // annars sparas ändringarna på standardspåret
            preserveDraftVersion: true
          }),
        });
        
        if (!response.ok) {
          throw new Error('Kunde inte spara innan sändning');
        }
        
        const savedEstimat = await response.json();
        if (!currentEstimatId && savedEstimat._id) {
          actualEstimatId = savedEstimat._id;
          setCurrentEstimatId(savedEstimat._id);
        }

        setHasUnsavedChanges(false);
        hannSpara = true;
      }
      
      // Om estimat redan skickats, spara osparade ändringar
      let estimatIdToSend = actualEstimatId;
      if (isActuallySent() && hasUnsavedChanges) {
        const updateRes = await fetch(`${API_URL}/api/estimat/${actualEstimatId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            beställare2: formatBeställare2ForAPI(formData.beställare2),
            projectInfo: formData.projectInfo,
            intervjuFrågor: currentStatus === 'draft' ? { ...formData.intervjuFrågor, dynamicNotes } : undefined,
            roomMeasurements: currentStatus === 'draft' ? roomMeasurements : undefined,
            estimatKategorier: formData.estimat,
            projectId: currentProjectId,
          })
        });
        
        if (!updateRes.ok) {
          throw new Error('Kunde inte uppdatera estimatet');
        }

        hannSpara = true;

        setHasUnsavedChanges(false);
      }

      // Bestäm lämplig status
      let targetStatus = 'sent';
      
      try {
        const checkResponse = await fetch(`${API_URL}/api/estimat/${estimatIdToSend}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (checkResponse.ok) {
          const currentEstimat = await checkResponse.json();
          // Ett uppföljningsmöte som redan varit hör till FÖREGÅENDE version.
          // Utan tidskontrollen ärvdes "Uppföljning bokad" in i den nya
          // versionen och kunden såg en badge för ett möte som redan ägt rum.
          const followupMeetingAt = currentEstimat.estimatFollowup?.meetingAt;
          const followupArAktuell = followupMeetingAt
            ? new Date(followupMeetingAt).getTime() >= Date.now()
            : Boolean(currentEstimat.estimatFollowup?.teamsJoinUrl);
          const hasFollowup = followupArAktuell;

          // En version som ALDRIG skickats är ny för kunden. Då kan det per
          // definition inte finnas feedback på den, hur mycket som än ärvts in
          // från föregående version.
          //
          // Utan den här kontrollen föddes varje ny version som
          // 'feedback_received', eftersom de ärvda kommentarerna räknades som
          // feedback. Kunden fick badgen "Feedback skickad" på ett estimat hen
          // aldrig svarat på, och upphandlingslistan visade fel steg.
          const arNyVersion = !currentEstimat.sentAt;

          if (hasFollowup) {
            targetStatus = 'followup_booked';
          } else if (!arNyVersion) {
            // Omskick av en redan skickad version — behåll feedback-läget så
            // att pågående dialog inte nollställs.
            const hasCustomerFeedback = currentEstimat.customerResponse?.comments &&
              Object.keys(currentEstimat.customerResponse.comments).length > 0;

            if (hasCustomerFeedback) {
              targetStatus = 'feedback_received';
            }
          }
        }
      } catch (e) {
        console.warn('Kunde inte kontrollera befintlig status, använder "sent"');
      }
      
      // Skicka övriga valda estimat först, med notiser avstängda. Utan det
      // skulle kunden få ett mail och en notis per estimat.
      const ovriga = Array.isArray(extraEstimat) ? extraEstimat.filter(e => e && e._id) : [];
      for (const extra of ovriga) {
        const extraRes = await fetch(`${API_URL}/api/estimat/${extra._id}/status`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: targetStatus,
            versionComment: versionComment || undefined,
            customerIntroMessage: customerIntroMessage || undefined,
            suppressCustomerNotification: true
          }),
        });
        if (!extraRes.ok) {
          throw new Error(`Kunde inte skicka estimatet ${extra.variantName || ''}`.trim());
        }
        antalOvrigaSkickade += 1;
      }

      // Uppdatera status (skickar utskick)
      const statusResponse = await fetch(`${API_URL}/api/estimat/${estimatIdToSend}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: targetStatus,
          versionComment: versionComment || undefined,
          customerIntroMessage: customerIntroMessage || undefined,
          // Namnen på de övriga så det enda mailet nämner allihop
          alsoSentVariantNames: ovriga.map(e => e.variantName).filter(Boolean)
        }),
      });

      if (!statusResponse.ok) {
        throw new Error('Kunde inte uppdatera estimat-status');
      }

      // Härifrån ÄR estimatet skickat och kunden har fått sitt mail. Allt som
      // följer är att läsa tillbaka det uppdaterade läget.
      huvudestimatSkickat = true;

      // Hämta det uppdaterade estimatet — samma spår, annars läses originalet
      const estimatUrl = variantKey
        ? `${API_URL}/api/estimat/lead/${leadId}?variantKey=${encodeURIComponent(variantKey)}`
        : `${API_URL}/api/estimat/lead/${leadId}`;
      const estimatResponse = await fetch(estimatUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (estimatResponse.ok) {
        const updatedEstimat = await estimatResponse.json();
        
        setCurrentSentAt(updatedEstimat.sentAt || null);
        setCurrentStatus(updatedEstimat.status || 'sent');
        setCurrentVersion(Number(updatedEstimat.version || currentVersion));
        if (updatedEstimat.projectId) setCurrentProjectId(updatedEstimat.projectId);
        
        // Invalidera cache
        if (invalidateCache) {
          await invalidateCache();
        }
        
        // Visa modal
        setModalIsUpdate(wasAlreadySent);
        setShowSuccessModal(true);
        setHasUnsavedChanges(false);
      } else {
        // Utskicket gick igenom men återläsningen misslyckades. Tidigare hände
        // ingenting här alls — varken bekräftelse eller fel — så BK trodde att
        // det gått fel och kunde skicka om, vilket ger kunden dubbla mail.
        setHasUnsavedChanges(false);
        alert('Estimatet är skickat till kunden, men vyn kunde inte uppdateras. '
          + 'Ladda om sidan för att se det nya läget — skicka INTE igen.');
      }
    } catch (error) {
      console.error('Fel vid skickande av estimat:', error);

      // Ett nätverksavbrott ger TypeError från fetch utan svar från servern
      // (ERR_NAME_NOT_RESOLVED, ERR_NETWORK_CHANGED vid byte av wifi eller VPN).
      // Det är inte samma sak som att backend nekat något, och ska inte
      // felsökas som det.
      const arNatverksfel = error instanceof TypeError;

      const delar = [];
      if (huvudestimatSkickat) {
        delar.push('Estimatet ÄR skickat till kunden. Ladda om sidan — skicka inte igen.');
      } else {
        delar.push(hannSpara
          ? 'Dina ändringar är sparade, men estimatet skickades INTE.'
          : 'Estimatet kunde varken sparas eller skickas.');
        if (antalOvrigaSkickade > 0) {
          delar.push(`${antalOvrigaSkickade} av de övriga valda estimaten hann skickas (utan mail till kunden).`);
        }
        delar.push(arNatverksfel
          ? 'Anropet nådde aldrig servern. Kontrollera nätverket — byte av wifi eller VPN mitt i räcker — och försök igen.'
          : `Servern svarade med ett fel: ${error.message}`);
      }

      alert(delar.join('\n\n'));
    } finally {
      setSaving(false);
    }
  }, [
    // variantKey MÅSTE vara med: se filens rubrik. Frys den i callbacken och en
    // sparning efter ett spårbyte hamnar på fel estimatspår.
    variantKey,
    setSaving,
    currentVersion,
    currentEstimatId,
    isActuallySent,
    leadId,
    formatBeställare2ForAPI,
    formData,
    currentStatus,
    dynamicNotes,
    roomMeasurements,
    setCurrentEstimatId,
    setHasUnsavedChanges,
    hasUnsavedChanges,
    currentProjectId,
    setCurrentSentAt,
    setCurrentStatus,
    setCurrentVersion,
    setCurrentProjectId,
    invalidateCache,
    setShowSuccessModal,
    setModalIsUpdate
  ]);

  return {
    handleSaveEstimat,
    handleSendEstimat,
  };
};

export default useEstimatSaveActions;
